import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface Player {
  id: string;
  name: string;
  color: string;
  position: number;
  avatar: string | null;
  pathIndex: number | null;
  money: number;
  job: {
    emoji: string;
  } | null;
}
interface PathStyle {
  name: string;
  emoji: string;
  glowColor: string;
  laneColor: string;
  edgeNeon: string;
  textColor: string;
  borderColor: string;
}
interface TileLite {
  id: number;
  name: string;
  category: string;
  effect: string;
  effectValue?: number;
}
interface RoadViewProps {
  paths: PathStyle[];
  activePathTiles: number[][];
  players: Player[];
  currentPlayerIndex: number;
  // null  = overview (show all 4 lanes)
  // 0..3  = zoomed into that player's chosen path
  focusedPathIndex: number | null;
  getTileById: (id: number) => TileLite;
  categoryStyles: Record<string, {
    icon: React.ElementType;
    bg: string;
    border: string;
  }>;
  playerColors: Record<string, string>;
}
const PATH_NEON_HEX = ['#a855f7', '#ec4899', '#f97316', '#06b6d4'];
const PAWN_COLORS: Record<string, string> = {
  pink: 'from-pink-400 via-pink-500 to-pink-700',
  purple: 'from-purple-400 via-purple-500 to-purple-700',
  blue: 'from-blue-400 via-blue-500 to-blue-700',
  teal: 'from-teal-400 via-teal-500 to-teal-700',
  gold: 'from-amber-400 via-amber-500 to-amber-700',
  coral: 'from-orange-400 via-rose-500 to-rose-700'
};
// Lane X positions on the cinematic city background, in OVERVIEW mode.
// Each path occupies one of four vertical lanes the artwork shows.
const OVERVIEW_LANE_X: Record<number, number> = {
  0: 22,
  1: 40,
  2: 60,
  3: 78 // Self-employed / Streamer
};
// In zoomed mode the active lane is centered on screen.
const ZOOMED_LANE_X = 50;
// Spacing along the lane (vertical pixels per tile).
const TILE_SPACING_PX_OVERVIEW = 36;
const TILE_SPACING_PX_ZOOMED = 110;
const HOP_DURATION_MS = 520;
// In zoomed view, the pawn locks to this % of the viewport height,
// and the lane scrolls underneath it so the pawn appears to walk forward.
const ZOOMED_PAWN_SCREEN_Y_PCT = 70;
// In overview, every lane spans this vertical range so all pawns stay visible.
const OVERVIEW_LANE_TOP_PCT = 18;
const OVERVIEW_LANE_BOTTOM_PCT = 86;
// ─────────────────────────────────────────────────────────────────────────────
// Pawn3D — a chunky cinematic pawn used in zoomed view.
function Pawn3D({
  color,
  avatar,
  letter,
  scale = 1,
  hopping
}: {
  color: string;
  avatar?: string | null;
  letter: string;
  scale?: number;
  hopping: boolean;
}) {
  return <motion.div className="relative flex flex-col items-center justify-end" style={{
    width: 110 * scale,
    height: 170 * scale
  }} animate={hopping ? {
    y: [0, -64 * scale, 0]
  } : {
    y: [0, -10 * scale, 0]
  }} transition={{
    duration: hopping ? HOP_DURATION_MS / 1000 : 1.6,
    repeat: hopping ? 0 : Infinity,
    ease: hopping ? [0.22, 1, 0.36, 1] : 'easeInOut'
  }} data-id="element-278">
      <div className="absolute bottom-0 bg-black/70 rounded-full blur-md" style={{
      width: 96 * scale,
      height: 22 * scale
    }} data-id="element-279" />
      <div className={`absolute bottom-1 rounded-full bg-gradient-to-b ${color}`} style={{
      width: 96 * scale,
      height: 34 * scale,
      boxShadow: 'inset 0 -5px 10px rgba(0,0,0,0.6)'
    }} data-id="element-280" />
      <div className={`absolute bg-gradient-to-b ${color}`} style={{
      bottom: 24 * scale,
      width: 34 * scale,
      height: 68 * scale,
      borderRadius: `${14 * scale}px ${14 * scale}px 0 0`,
      boxShadow: 'inset -5px 0 10px rgba(0,0,0,0.5)'
    }} data-id="element-281" />
      <div className={`absolute rounded-full bg-gradient-to-b ${color}`} style={{
      bottom: 78 * scale,
      width: 68 * scale,
      height: 24 * scale,
      boxShadow: 'inset 0 -3px 5px rgba(0,0,0,0.5)'
    }} data-id="element-282" />
      <div className={`absolute rounded-full bg-gradient-to-br ${color} flex items-center justify-center text-white font-black overflow-hidden border-2 border-white/40`} style={{
      bottom: 92 * scale,
      width: 76 * scale,
      height: 76 * scale,
      fontSize: 24 * scale,
      boxShadow: 'inset -5px -5px 12px rgba(0,0,0,0.5), inset 5px 5px 12px rgba(255,255,255,0.5), 0 10px 20px rgba(0,0,0,0.6)'
    }} data-id="element-283">
        {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" data-id="element-284" /> : letter}
      </div>
      <motion.div className="absolute rounded-full pointer-events-none" style={{
      bottom: 60 * scale,
      width: 130 * scale,
      height: 130 * scale,
      background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)'
    }} animate={{
      scale: [1, 1.5, 1],
      opacity: [0.8, 0, 0.8]
    }} transition={{
      duration: 1.6,
      repeat: Infinity
    }} data-id="element-285" />
    </motion.div>;
}
// ─────────────────────────────────────────────────────────────────────────────
// MiniPawn — a small chibi pawn used in overview mode.
function MiniPawn({
  color,
  letter,
  avatar,
  isActive
}: {
  color: string;
  letter: string;
  avatar?: string | null;
  isActive: boolean;
}) {
  return <motion.div className={`relative rounded-full bg-gradient-to-br ${color} flex items-center justify-center text-white text-[11px] font-black border-2 border-white/70 overflow-hidden`} style={{
    width: isActive ? 30 : 24,
    height: isActive ? 30 : 24,
    boxShadow: isActive ? '0 0 14px rgba(255,255,255,0.9), 0 4px 10px rgba(0,0,0,0.5)' : '0 3px 6px rgba(0,0,0,0.5)'
  }} animate={isActive ? {
    y: [0, -3, 0]
  } : undefined} transition={{
    duration: 1.2,
    repeat: isActive ? Infinity : 0,
    ease: 'easeInOut'
  }} data-id="element-286">
      {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" data-id="element-287" /> : letter}
    </motion.div>;
}
// ─────────────────────────────────────────────────────────────────────────────
// Hook: animates a "displayed" position toward a "target" position, one tile
// at a time, so the pawn visibly walks square-by-square instead of teleporting.
function useStepAnimation(targetPos: number, resetKey: string): {
  displayPos: number;
  hopping: boolean;
} {
  const [displayPos, setDisplayPos] = useState(targetPos);
  const [hopping, setHopping] = useState(false);
  const lastResetKeyRef = useRef(resetKey);
  // Snap (no animation) when the resetKey changes — e.g. when the active
  // player switches and we should jump to the new player's stored position.
  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      setDisplayPos(targetPos);
      setHopping(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  useEffect(() => {
    if (displayPos === targetPos) {
      setHopping(false);
      return;
    }
    setHopping(true);
    const timer = setTimeout(() => {
      setDisplayPos(prev => {
        if (prev === targetPos) return prev;
        return prev + (targetPos > prev ? 1 : -1);
      });
    }, HOP_DURATION_MS);
    return () => clearTimeout(timer);
  }, [displayPos, targetPos]);
  return {
    displayPos,
    hopping
  };
}
// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
export function RoadView(props: RoadViewProps) {
  const {
    paths,
    players,
    currentPlayerIndex,
    activePathTiles,
    focusedPathIndex
  } = props;
  const currentPlayer = players[currentPlayerIndex];
  const isZoomed = focusedPathIndex !== null && focusedPathIndex !== undefined;
  // The path the camera is currently focused on. In zoomed mode this is the
  // active player's chosen path; in overview mode this is unused.
  const activePathIdx = isZoomed ? focusedPathIndex! : currentPlayer.pathIndex !== null && currentPlayer.pathIndex !== undefined ? currentPlayer.pathIndex : 0;
  const neon = PATH_NEON_HEX[activePathIdx] ?? '#a855f7';
  const zoomedTilesOnPath = activePathTiles[activePathIdx] ?? [];
  const totalTilesZoomed = zoomedTilesOnPath.length || 30;
  // Animate the active player's walking — only matters in zoomed mode.
  const stepAnim = useStepAnimation(currentPlayer.position, `${currentPlayer.id}-${activePathIdx}`);
  const {
    displayPos: zoomedDisplayPos,
    hopping
  } = stepAnim;
  return <div className="absolute inset-0 overflow-hidden z-[5] pointer-events-none" data-id="element-288">
      {/* ════════════════════════════════════════════════════════════════ */}
      {/* OVERVIEW MODE — show all four lanes & all players at once.       */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-289">
        {!isZoomed && <motion.div key="overview" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5
      }} className="absolute inset-0" data-id="element-290">
            {/* Render each lane's pawns (no visible track lines — the
                background art IS the board). */}
            {[0, 1, 2, 3].map(laneIdx => {
          const laneX = OVERVIEW_LANE_X[laneIdx];
          const tilesOnLane = activePathTiles[laneIdx] ?? [];
          const totalTilesLane = tilesOnLane.length || 30;
          const playersOnLane = players.filter(p => p.pathIndex === laneIdx);
          return <Fragment key={laneIdx} data-id="element-291">
                  {playersOnLane.map((p, stackIdx) => {
              // Map tile index along lane → vertical % on screen.
              // tile 0 sits at lane bottom, last tile at lane top.
              const progress = p.position / Math.max(1, totalTilesLane - 1);
              const yPct = OVERVIEW_LANE_BOTTOM_PCT - progress * (OVERVIEW_LANE_BOTTOM_PCT - OVERVIEW_LANE_TOP_PCT);
              // Stack multiple players on the same tile horizontally.
              const xOffsetPx = (stackIdx - (playersOnLane.length - 1) / 2) * 18;
              const isActive = players[currentPlayerIndex]?.id === p.id;
              return <motion.div key={p.id} className="absolute" style={{
                left: `${laneX}%`,
                top: `${yPct}%`,
                transform: `translate(calc(-50% + ${xOffsetPx}px), -50%)`,
                filter: isActive ? `drop-shadow(0 0 10px ${PATH_NEON_HEX[laneIdx]})` : undefined
              }} animate={{
                left: `${laneX}%`,
                top: `${yPct}%`
              }} transition={{
                duration: HOP_DURATION_MS / 1000,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-292">
                        <MiniPawn color={PAWN_COLORS[p.color] || PAWN_COLORS.purple} letter={p.name.charAt(0).toUpperCase()} avatar={p.avatar} isActive={isActive} data-id="element-293" />
                      </motion.div>;
            })}
                </Fragment>;
        })}

            {/* Lane labels at the bottom — tiny chips that sit under each
                visual lane on the background art, telling players which
                lane is which. They're small and sit out of the way. */}
            <div className="absolute bottom-3 left-0 right-0 flex justify-center pointer-events-none" data-id="element-294">
              <div className="flex gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/10" data-id="element-295">
                {paths.map((p, i) => {
              const isCurrentLane = currentPlayer.pathIndex === i;
              return <div key={i} className="flex items-center gap-1 text-[10px] font-black" style={{
                color: PATH_NEON_HEX[i],
                opacity: isCurrentLane ? 1 : 0.55
              }} data-id="element-296">
                      <span data-id="element-297">{p.emoji}</span>
                      <span className="hidden sm:inline" data-id="element-298">
                        {p.name.split(' →')[0]}
                      </span>
                    </div>;
            })}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ZOOMED MODE — fullscreen view of the active player's lane,      */}
      {/* with the big cinematic walking pawn.                            */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-299">
        {isZoomed && <motion.div key={`zoomed-${activePathIdx}`} initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 1.06
      }} transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0" data-id="element-300">
            {/* Neon vignette — visually narrows the world to one lane. */}
            <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at 50% 70%, transparent 0%, transparent 35%, ${neon}22 60%, rgba(10,4,28,0.7) 100%)`
        }} data-id="element-301" />

            {/* Invisible track — the lane scrolls vertically as the pawn
                walks, but no overlay markers are drawn. The city art IS
                the board. */}
            <motion.div className="absolute" style={{
          left: `${ZOOMED_LANE_X}%`,
          top: `${ZOOMED_PAWN_SCREEN_Y_PCT}%`,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0
        }} animate={{
          y: zoomedDisplayPos * TILE_SPACING_PX_ZOOMED
        }} transition={{
          duration: HOP_DURATION_MS / 1000,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-302" />

            {/* Active player pawn — walks tile-by-tile in this view. */}
            <motion.div className="absolute" style={{
          left: `${ZOOMED_LANE_X}%`,
          top: `${ZOOMED_PAWN_SCREEN_Y_PCT}%`,
          transform: 'translate(-50%, -100%)',
          filter: `drop-shadow(0 0 28px ${neon}cc)`
        }} data-id="element-303">
              <Pawn3D color={PAWN_COLORS[currentPlayer.color] || PAWN_COLORS.purple} avatar={currentPlayer.avatar} letter={currentPlayer.name.charAt(0).toUpperCase()} scale={1} hopping={hopping} data-id="element-304" />
            </motion.div>

            {/* Path indicator (bottom-left) */}
            <div className="absolute bottom-32 left-3 md:left-6 z-30" data-id="element-305">
              <div className="rounded-2xl p-4 backdrop-blur-md border-2" style={{
            background: 'rgba(15,8,35,0.85)',
            borderColor: `${neon}66`,
            boxShadow: `0 8px 30px rgba(0,0,0,0.5), 0 0 30px ${neon}44, inset 0 1px 0 rgba(255,255,255,0.1)`
          }} data-id="element-306">
                <p className="text-white/70 font-black text-[10px] tracking-widest mb-2 leading-tight" data-id="element-307">
                  ON THE ROAD
                </p>
                <div className="flex items-center gap-2" data-id="element-308">
                  <span className="text-2xl" data-id="element-309">
                    {paths[activePathIdx]?.emoji}
                  </span>
                  <span className="text-base font-black uppercase tracking-wider" style={{
                color: neon,
                textShadow: `0 0 12px ${neon}`
              }} data-id="element-310">
                    {paths[activePathIdx]?.name?.split(' →')[0]}
                  </span>
                </div>
                <div className="mt-2 text-white/70 text-[11px] font-black tracking-wider" data-id="element-311">
                  TILE {zoomedDisplayPos + 1} / {totalTilesZoomed}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* HUD: Player chips (always visible, both modes)                  */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className="absolute top-4 right-4 md:right-8 z-30 flex flex-col gap-2" data-id="element-312">
        {players.map((p, i) => {
        const isActive = i === currentPlayerIndex;
        const playerPathColor = p.pathIndex !== null ? PATH_NEON_HEX[p.pathIndex] : '#888';
        return <div key={p.id} className="rounded-full px-4 py-2 backdrop-blur-md border-2 flex items-center gap-3 transition-all duration-500" style={{
          background: isActive ? `${playerPathColor}ee` : 'rgba(15,8,35,0.85)',
          borderColor: isActive ? '#fff' : 'rgba(255,255,255,0.2)',
          boxShadow: isActive ? `0 0 24px ${playerPathColor}cc` : '0 4px 10px rgba(0,0,0,0.4)',
          transform: isActive ? 'scale(1.05)' : 'scale(1)'
        }} data-id="element-313">
              <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${PAWN_COLORS[p.color] || PAWN_COLORS.purple} text-white text-xs font-black flex items-center justify-center border-2 border-white/40 shadow-inner`} data-id="element-314">
                {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full rounded-full object-cover" data-id="element-315" /> : p.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col" data-id="element-316">
                <span className="text-[10px] font-bold text-white/80 leading-none mb-0.5" data-id="element-317">
                  {p.name}
                </span>
                <span className="text-sm font-black leading-none" style={{
              color: p.money < 0 ? '#fca5a5' : isActive ? '#fff' : '#a7f3d0',
              textShadow: isActive ? '0 1px 2px rgba(0,0,0,0.4)' : 'none'
            }} data-id="element-318">
                  ${p.money}
                </span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}